import type { MetaFunction } from '@remix-run/node';

import type { StaticPageLoaderData, staticPageLoader } from '~/services/static_page';

import getCanonicalUrl from '../getCanonicalUrl';
import getMergedMetaData from '../getMergedMetaData';
import getMetaDataLinks from '../getMetaDataLinks';

function getStructuredDataByPage(pageSlug: string, loaderData: StaticPageLoaderData) {
  switch (pageSlug) {
    case '/archive':
      return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@id': loaderData?.site?.publication?.url,
              name: loaderData?.site?.publication?.name,
            },
          },
        ],
      };
    default:
      return undefined;
  }
}

interface DynamicMetaDataOverrides {
  title?: string;
  description?: string;
  // Add other potential overrides here
}

const getStaticPageMetaData = (
  { matches, data }: Parameters<MetaFunction<typeof staticPageLoader>>[0],
  overrideData?: DynamicMetaDataOverrides,
) => {
  const requestUrl = data?.requestUrl;
  const canonicalUrl = getCanonicalUrl(requestUrl);
  const url = new URL(requestUrl);
  const pageSlug = url.pathname;
  const siteVersion = data?.site?.viewable_site_version;
  const siteMetadata = siteVersion?.metadata;
  const pageVersion = data?.page?.viewable_page_version;
  const pageMetadata = pageVersion?.metadata;

  const getMetadata = (key: string) => {
    return pageMetadata?.[key] || siteMetadata?.[key] || '';
  };

  return getMergedMetaData({
    canonicalUrl,
    matches,
    overrides: {
      ...(getMetadata('noindex_enabled') === true ? { robots: 'noindex' } : {}),
      title: overrideData?.title || getMetadata('meta_title') || pageVersion?.name,
      description: getMetadata('meta_description') || pageVersion?.description,
      'og:title': overrideData?.title || getMetadata('facebook_title') || pageVersion?.name,
      'og:description': getMetadata('facebook_description') || pageVersion?.description,
      'og:image': getMetadata('facebook_image_url'),
      'og:url': canonicalUrl,
      'twitter:title': overrideData?.title || getMetadata('x_title'),
      'twitter:description': getMetadata('x_description'),
      'twitter:image': getMetadata('x_image_url'),
      'twitter:creator': getMetadata('x_username'),
      'twitter:url': canonicalUrl,
    },
    links: getMetaDataLinks({ renderableFonts: data?.renderableFonts || [] }),
    structuredData: getStructuredDataByPage(pageSlug, data) || {
      url: requestUrl,
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${requestUrl}?q={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    },
  });
};

export default getStaticPageMetaData;
